// W12 step A5: putting a resolved store to work. Quil's own file (not canonical).
//
// `quilScope.ts` DECIDES which store an account opens (and is pure of auth and OneDrive, so it can be
// proved on its own). This file PERFORMS the binding that follows, in one place and one order, so
// nothing is half-bound:
//
//   1. the database          `setActiveDb`        the stand-in every `db.` reference reaches through
//   2. the lock              `bindCoordination`   `quil:<store>:sync`
//   3. the token             `bindTokenScope`     the store's account by id, never the active one
//   4. the wire guard        `setRequestGuard`    the owner stamp is re-checked before EVERY request
//   5. read-only             `setReadOnly`        what the pill says, and what refuses every write
//
// A read-only store (data from a newer build, or one that could not be upgraded safely) gets a guard
// that refuses every request outright: read-only means nothing is changed locally NOR remotely.
//
// It is the provider's default `StoreResolver`. Tests give the provider their own.
import { bindTokenScope, unbindTokenScope, QUIL_ONEDRIVE_ROOT } from './auth'
import { assertStoreOwner, findOwnedStore, legacyOwner, openStore, ReadOnlyError, resolveOwnedStore, resolveStore } from './quilScope'
import type { ReadOnlyInfo, ResolvedStore } from './quilScope'
import { listChildFolders, setRequestGuard } from './onedrive'
import { noteNewerData, setReadOnly } from './readOnly'
import { BASE, LAST_ACCOUNT_KEY } from './scopeKeys'
import { getActiveDb, setActiveDb } from './db'
import { bindCoordination } from './syncCoordination'
import { prospectiveScope } from './scopedToken'
import type { StoreScope } from './storageScope'

/** What the provider holds once a store is bound. */
export interface BoundStore {
  /** The account the store is bound to; `''` for a local-only build (no sign-in at all). */
  accountId: string
  scope: StoreScope
  /** Pre-W12 data of unprovable ownership is being held (Settings shows the held-data section). */
  held: boolean
  /** Non-null when nothing may be written, locally or on OneDrive. */
  readOnly: ReadOnlyInfo | null
  /** Opened with no sign-in (an offline cold start): it can be read and edited here, and cannot sync. */
  offline: boolean
  /** Let go of this tab's connection to the store (a connection left open blocks deleting it). */
  release: () => void
}

export interface StoreResolver {
  /** Resolve and bind the store a signed-in account opens. Rejects with `StoreUnavailableError`; nothing is changed. */
  bind(accountId: string): Promise<BoundStore>
  /** The dev bypass (and tests): the one local store, no account. */
  bindLocalOnly(): Promise<BoundStore>
  /** Does this device already hold a store it knows is an account's, for an offline cold start? */
  canOpenOffline(): Promise<boolean>
  /** No sign-in at all (offline cold start): the store the device last opened, if it is already that account's. */
  bindOffline(): Promise<BoundStore | null>
}

const LOCAL_ONLY: StoreScope = { accountId: '', storeName: BASE, legacy: true }

function reloadForOtherAccount(accountId: string): (activeId: string | null) => void {
  // A null active account must NOT reload (a sign-out in flight would loop); only a different one.
  return (activeId) => {
    if (activeId !== null && activeId !== accountId) window.location.reload()
  }
}

function rememberAccount(accountId: string): void {
  try {
    localStorage.setItem(LAST_ACCOUNT_KEY, accountId)
  } catch {
    /* a device pointer only: without it an offline cold start simply has nothing to reopen */
  }
}

function lastAccount(): string | null {
  try {
    return localStorage.getItem(LAST_ACCOUNT_KEY)
  } catch {
    return null
  }
}

/** Perform a resolution: bind everything to it, in order.
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export function applyResolved(resolved: ResolvedStore, accountId: string | null, offline = false): BoundStore {
  const { scope, db } = resolved
  // What the session is open as is recorded BEFORE the store becomes visible, so whatever reacts to
  // the binding (the shell's boot effect) already sees whether it may write.
  setReadOnly(resolved.readOnly)
  setActiveDb(db)
  bindCoordination(scope)
  if (accountId) bindTokenScope(scope, { accountId, onAccountChanged: reloadForOtherAccount(accountId) })
  else unbindTokenScope()
  // The owner check guards a token that carries an ACCOUNT's identity. With no account (the offline
  // cold start, the dev bypass) no token is bound at all, so a request is refused as "sign in" by the
  // token getter itself, exactly as it was before W12; the owner check would only turn that quiet
  // pause into a ScopeMismatchError on a store that has no account to mismatch.
  setRequestGuard(
    resolved.readOnly
      ? () => Promise.reject(new ReadOnlyError(resolved.readOnly?.reason))
      : accountId
        ? () => assertStoreOwner(db, scope)
        : null,
  )
  if (accountId && !offline) rememberAccount(accountId)
  return { accountId: accountId ?? '', scope, held: resolved.held, readOnly: resolved.readOnly, offline, release: () => db.close() }
}

/**
 * One resolution per account per page load: StrictMode's two effect passes (and a retry racing a
 * slow first attempt) would otherwise each back up, migrate and stamp the same store.
 */
const inflight = new Map<string, Promise<BoundStore>>()
let offlineInflight: Promise<BoundStore | null> | null = null

/** This account's works on OneDrive (folder names), for P3. */
async function listRemoteWorkIds(): Promise<ReadonlySet<string>> {
  return new Set(await listChildFolders(`${QUIL_ONEDRIVE_ROOT}/works`))
}

export const defaultStoreResolver: StoreResolver = {
  bind(accountId) {
    const existing = inflight.get(accountId)
    if (existing) return existing
    const p = (async () => {
      // P3's listing is made under THIS account's token, before any store is decided.
      bindTokenScope(prospectiveScope(BASE, accountId), { accountId, onAccountChanged: reloadForOtherAccount(accountId) })
      let resolved: ResolvedStore
      try {
        resolved = await resolveStore(accountId, { listRemote: listRemoteWorkIds, onNewerData: noteNewerData })
      } catch (e) {
        unbindTokenScope()
        throw e
      }
      return applyResolved(resolved, accountId)
    })()
    inflight.set(accountId, p)
    p.catch(() => inflight.delete(accountId)) // a failure is retried, never cached
    return p
  },

  async bindLocalOnly() {
    // Already bound (a test, or a second pass): nothing to open.
    const existing = getActiveDb()
    if (existing) return { accountId: '', scope: LOCAL_ONLY, held: false, readOnly: null, offline: false, release: () => existing.close() }
    const opened = await openStore(BASE, { onNewerData: noteNewerData })
    const resolved: ResolvedStore = { scope: LOCAL_ONLY, decision: { kind: 'open-legacy', name: BASE }, held: false, ...opened }
    return applyResolved(resolved, null)
  },

  async canOpenOffline() {
    try {
      const account = lastAccount() ?? (await legacyOwner())
      return account ? (await findOwnedStore(account)) !== null : false
    } catch {
      return false
    }
  },

  bindOffline() {
    // Once per page load, for the same reason as `bind`: StrictMode's second effect pass would open
    // a second connection to the same store and leave the first open, which blocks deleting it.
    offlineInflight ??= (async () => {
      // The pointer of the last sign-in, else (a device already in use before W12, on its first W12
      // boot) the account the legacy store is stamped for: pre-W12 that device was trusted the same way.
      const account = lastAccount() ?? (await legacyOwner())
      if (!account) return null
      const resolved = await resolveOwnedStore(account, { onNewerData: noteNewerData })
      return resolved ? applyResolved(resolved, null, true) : null
    })()
    const p = offlineInflight
    p.catch(() => { offlineInflight = null }) // a failure is retried, never cached
    return p
  },
}

/** Tests, and nothing else: forget this page load's resolutions.
 * @internal Test seam: resets module state between tests. Never imported by the app.
 */
export function resetStoreBindingForTests(): void {
  inflight.clear()
  offlineInflight = null
  setRequestGuard(null)
  unbindTokenScope()
  bindCoordination(null)
  setReadOnly(null)
}
